import React, { useState, useEffect } from "react";
import api from "../../globals/axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Pagination,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  LocalShipping,
  PersonPin,
  Phone,
  Email,
  Home,
  Inventory,
  Search,
  Clear,
} from "@mui/icons-material";
import CustomAppBar from "../../components/AppBar";
import GenericButton from "../../components/Custom/GenericButton";
import { useTranslation } from "react-i18next";

const BiocharForms = () => {
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalObjects: 0,
  });

  const fetchForms = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true);
      let url = `/biochar/biochar-interest/?page=${page}`;
      if (searchTerm) {
        url += `&search=${searchTerm}`;
      }
      const response = await api.get(url);
      const allForms = response.data.results;
      setForms(allForms);

      setPagination({
        currentPage: response.data.current_page_number,
        totalPages: response.data.total_pages,
        totalObjects: response.data.total_objects,
      });
    } catch (error) {
      console.error("Error fetching biochar interest forms:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handlePageChange = (value) => {
    fetchForms(value);
  };

  const clearSearch = () => {
    setSearchTerm("");
    fetchForms();
  };

  const getDeliveryModeLabel = (mode) => {
    return mode === "SELFPICKUP" ? "Self Pickup" : "Delivery";
  };

  const getDeliveryModeColor = (mode) => {
    return mode === "SELFPICKUP" ? "warning" : "success";
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight="bold"
            color="primary"
          >
            Biochar Interest Forms
          </Typography>

          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, farm name or phone number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={clearSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: 2 },
              }}
            />
            <GenericButton
              backgroundColor={"#4CAF50"}
              fontColor={"#fff"}
              fontSize={"14px"}
              padding={"14px"}
              onClick={() => fetchForms(1, searchTerm)}
            >
              {t("Search")}
            </GenericButton>
          </Box>

          <Typography variant="subtitle1" color="text.secondary" mb={3}>
            Total forms: {pagination.totalObjects}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={3}>
                {forms.map((form) => (
                  <Grid item xs={12} key={form.id}>
                    <Card
                      elevation={2}
                      sx={{
                        borderRadius: 2,
                        transition: "transform 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 8,
                        },
                      }}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                          }}
                        >
                          <Box>
                            <Typography variant="h6" fontWeight="bold">
                              {form.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {form.id}
                            </Typography>
                          </Box>
                          <Chip
                            icon={
                              form.preferred_mode_of_delivery ===
                              "SELFPICKUP" ? (
                                <PersonPin />
                              ) : (
                                <LocalShipping />
                              )
                            }
                            label={getDeliveryModeLabel(
                              form.preferred_mode_of_delivery
                            )}
                            color={getDeliveryModeColor(
                              form.preferred_mode_of_delivery
                            )}
                            size="medium"
                          />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Home sx={{ mr: 1, color: "primary.main" }} />
                              <Typography variant="body1" fontWeight="medium">
                                {form.farm_name
                                  ? `Farm: ${form.farm_name}`
                                  : `Name: ${form.name}`}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Phone sx={{ mr: 1, color: "primary.main" }} />
                              <Typography variant="body2">
                                {form.phone_number}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Email sx={{ mr: 1, color: "primary.main" }} />
                              <Typography variant="body2">
                                {form.email}
                              </Typography>
                            </Box>

                            {form.organization && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Organization:</strong>{" "}
                                {form.organization}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Inventory
                                sx={{ mr: 1, color: "primary.main" }}
                              />
                              <Typography variant="body1" fontWeight="medium">
                                Quantity: {form.quantity_required} kg
                              </Typography>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Location:</strong> {form.village},{" "}
                              {form.district_name}, {form.state_name},{" "}
                              {form.pincode}
                            </Typography>

                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Address:</strong> {form.address_line1},{" "}
                              {form.postoffice_name}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {searchTerm && forms.length === 0 && (
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No results found for &quot;{searchTerm}&quot;
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              {pagination.totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton={false}
                    showLastButton={false}
                  />
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default BiocharForms;
